import { useMemo } from 'react';
import { ExportAsCSVButton } from '../../ExportAsCSVButton';
import { deriveCsvColumns } from '../../csv';

/**
 * Shared card chrome for the dense extraction-table tier. Wraps a bespoke
 * table (AD, SB, LLP, part movement, engine, LDND, ARC, Bill of Sale, QEC) in
 * the rounded/bordered panel used on the document-page view, with a header that
 * shows "Label (N)" on the left and an "Export as CSV" button on the right.
 *
 * CSV columns are derived as the union of every row's keys (matching the old
 * `LifecycleEventsTable` behaviour) so rows that omit a field others populate
 * still contribute their column. Non-scalar values (nested objects, arrays such
 * as LLP `engineVariants`) are JSON-stringified by `ExportAsCSVButton`.
 */
type ExtractionTableBodyHeight = 'consolidated' | 'project';

const bodyClassNames: Record<ExtractionTableBodyHeight, string> = {
  consolidated: 'max-h-[400px] overflow-auto',
  project: 'max-h-[650px] overflow-auto',
};

export function ExtractionTableCard<T extends Record<string, unknown>>({
  label,
  rows,
  csvTitle,
  children,
  bodyHeight,
}: {
  label: string;
  rows: T[];
  csvTitle: string;
  children: React.ReactNode;
  /**
   * Controls vertical table-body scrolling by view. Leave unset for natural
   * document-page height, use `consolidated` for a 400px cap, and use `project`
   * for a 650px cap.
   */
  bodyHeight?: ExtractionTableBodyHeight;
}) {
  const columns = useMemo(() => deriveCsvColumns(rows), [rows]);

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between gap-4">
        <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
          {label} ({rows.length})
        </h2>
        <ExportAsCSVButton data={rows} columns={columns} title={csvTitle} />
      </div>
      <div className={bodyHeight ? bodyClassNames[bodyHeight] : 'overflow-x-auto'}>
        {children}
      </div>
    </div>
  );
}
